import { supabase } from "./supabaseClient";
import type {
  AdiarLeadResultado,
  ContactChannel,
  DadosEventoCalendar,
  ExcluirDadosTitularResultado,
  ExportarDadosTitularResultado,
  ImportarLoteResumo,
  MarcarNaoContatarResultado,
  ResolverDuplicataAcao,
  ResolverDuplicataResultado,
  RevogarNaoContatarResultado,
  Script,
} from "./types";

export interface ImportarLoteInput {
  nome: string;
  origem_fonte: string;
  origem_detalhe: string;
  origem_coletada_em: string;
  linhas: Record<string, unknown>[];
  observacao?: string | null;
}

export async function importarLote(
  input: ImportarLoteInput,
): Promise<ImportarLoteResumo> {
  const { data, error } = await supabase.rpc("importar_lote", {
    p_nome: input.nome,
    p_origem_fonte: input.origem_fonte,
    p_origem_detalhe: input.origem_detalhe,
    p_origem_coletada_em: input.origem_coletada_em,
    p_linhas: input.linhas,
    p_observacao: input.observacao ?? null,
  });
  if (error) throw new Error(error.message);
  return data as ImportarLoteResumo;
}

export async function resolverDuplicata(
  itemId: string,
  acao: ResolverDuplicataAcao,
  observacao?: string,
): Promise<ResolverDuplicataResultado> {
  const { data, error } = await supabase.rpc("resolver_duplicata", {
    p_item_id: itemId,
    p_acao: acao,
    p_observacao: observacao ?? null,
  });
  if (error) throw new Error(error.message);
  return data as ResolverDuplicataResultado;
}

export async function marcarNaoContatar(
  leadId: string,
  motivo: string,
): Promise<MarcarNaoContatarResultado> {
  const { data, error } = await supabase.rpc("marcar_nao_contatar", {
    p_lead_id: leadId,
    p_motivo: motivo,
  });
  if (error) throw new Error(error.message);
  return data as MarcarNaoContatarResultado;
}

export async function revogarNaoContatar(
  supressaoId: string,
  motivo: string,
): Promise<RevogarNaoContatarResultado> {
  const { data, error } = await supabase.rpc("revogar_nao_contatar", {
    p_supressao_id: supressaoId,
    p_motivo: motivo,
  });
  if (error) throw new Error(error.message);
  return data as RevogarNaoContatarResultado;
}

export async function exportarDadosTitular(
  leadId: string,
): Promise<ExportarDadosTitularResultado> {
  const { data, error } = await supabase.rpc("exportar_dados_titular", {
    p_lead_id: leadId,
  });
  if (error) throw new Error(error.message);
  return data as ExportarDadosTitularResultado;
}

export async function excluirDadosTitular(
  leadId: string,
  criarSupressao: boolean,
  motivo?: string,
): Promise<ExcluirDadosTitularResultado> {
  const { data, error } = await supabase.rpc("excluir_dados_titular", {
    p_lead_id: leadId,
    p_criar_supressao: criarSupressao,
    p_motivo: motivo ?? null,
  });
  if (error) throw new Error(error.message);
  return data as ExcluirDadosTitularResultado;
}

// ---------------------------------------------------------------------------
// v1.1
// ---------------------------------------------------------------------------

/** Adiamento rapido ("snooze"). SEMPRE via RPC, nunca update direto em
 * proximo_contato_em: update direto sob RLS que a policy bloqueia retorna
 * sucesso com zero linhas (o mesmo gotcha tratado em db.ts) — aqui o backend
 * faz o `not found` virar excecao de verdade. Tambem falha alto para lead
 * suprimido/arquivado, em vez de "adiar" silenciosamente sem efeito. */
export async function adiarLead(
  leadId: string,
  opcoes: { dias: number } | { ate: string },
): Promise<AdiarLeadResultado> {
  const { data, error } = await supabase.rpc("adiar_lead", {
    p_lead_id: leadId,
    p_dias: "dias" in opcoes ? opcoes.dias : null,
    p_ate: "ate" in opcoes ? opcoes.ate : null,
  });
  if (error) throw new Error(error.message);
  return data as AdiarLeadResultado;
}

/** Scripts aplicaveis a um ramo, do mais especifico para o mais generico —
 * RPC (nao select direto) porque depende de parametro. */
export async function scriptsParaRamo(
  ramo: string | null,
  canal?: ContactChannel | null,
): Promise<Script[]> {
  const { data, error } = await supabase.rpc("scripts_para_ramo", {
    p_ramo: ramo,
    p_canal: canal ?? null,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as Script[];
}

/** Caminho A do Google Calendar: devolve os CAMPOS do evento (nao a URL
 * pronta) — o front monta o link com encodeURIComponent, ver
 * docs/google-calendar-setup.md. Falha se o lead nao tiver reuniao_em. */
export async function dadosEventoCalendar(
  leadId: string,
  duracaoMin = 60,
): Promise<DadosEventoCalendar> {
  const { data, error } = await supabase.rpc("dados_evento_calendar", {
    p_lead_id: leadId,
    p_duracao_min: duracaoMin,
  });
  if (error) throw new Error(error.message);
  return data as DadosEventoCalendar;
}
